import * as RadixSelect from "@radix-ui/react-select";
import { useId, type ReactNode } from "react";

import { Mini } from "../../icons";
import { cn } from "../../lib/cn";
import { useNearestDialogPortalContainer } from "../Popover";

export type SelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type SelectAction = {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  tone?: "default" | "accent" | "danger";
};

export type SelectProps<T extends string> = {
  label: string;
  labelHidden?: boolean;
  value: T;
  options: SelectOption<T>[];
  actions?: SelectAction[];
  onChange: (value: T) => void;
  className?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
};

const actionToneClassName: Record<NonNullable<SelectAction["tone"]>, string> = {
  default: "text-text",
  accent: "text-accent font-medium",
  danger:
    "text-error-text data-[highlighted]:bg-error-surface data-[highlighted]:text-error-text",
};

export function Select<T extends string>({
  label,
  labelHidden = false,
  value,
  options,
  actions = [],
  onChange,
  className,
  placeholder = "Select…",
  helperText,
  error,
  disabled = false,
  required = false,
}: SelectProps<T>) {
  const labelId = useId();
  const [rootRef, portalContainer] =
    useNearestDialogPortalContainer<HTMLDivElement>();
  const messageId = error
    ? `${labelId}-error`
    : helperText
      ? `${labelId}-help`
      : undefined;
  const getActionValue = (index: number) => `${labelId}-action-${index}`;

  return (
    <div
      ref={rootRef}
      data-required={required ? true : undefined}
      className={cn("flex flex-col gap-1.5", className)}
    >
      <span
        id={labelId}
        className={cn("text-label text-text", labelHidden && "sr-only")}
      >
        {label}
      </span>
      <RadixSelect.Root
        value={value}
        onValueChange={(nextValue) => {
          const actionIndex = actions.findIndex(
            (_action, index) => getActionValue(index) === nextValue,
          );
          if (actionIndex >= 0) {
            const action = actions[actionIndex];
            if (!action.disabled) action.onSelect();
            return;
          }

          onChange(nextValue as T);
        }}
        disabled={disabled}
      >
        <RadixSelect.Trigger
          aria-labelledby={labelId}
          aria-required={required ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={messageId}
          disabled={disabled}
          className={cn(
            "bg-surface text-body text-text focus:ring-accent data-[placeholder]:text-placeholder flex h-10 w-full items-center justify-between gap-2 rounded-md border border-separator px-3 focus:outline-none focus:ring-2 focus:ring-inset",
            "disabled:bg-fill disabled:text-text-tertiary disabled:cursor-not-allowed",
            error && "ring-error ring-1 ring-inset",
          )}
        >
          <RadixSelect.Value placeholder={placeholder} />
          <RadixSelect.Icon>
            <Mini.ChevronUpDownIcon
              className="text-text-tertiary size-5"
              aria-hidden="true"
            />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal container={portalContainer ?? undefined}>
          <RadixSelect.Content
            position="popper"
            sideOffset={4}
            className="border-separator bg-surface shadow-overlay data-[state=open]:animate-fade-in z-50 max-h-60 min-w-[--radix-select-trigger-width] overflow-auto rounded-lg border py-1 motion-reduce:animate-none"
          >
            <RadixSelect.Viewport>
              {options.map((o) => (
                <RadixSelect.Item
                  key={o.value}
                  value={o.value}
                  disabled={o.disabled}
                  className="text-body text-text data-[highlighted]:bg-fill flex cursor-pointer items-center justify-between gap-4 px-3 py-2 outline-none data-[disabled]:cursor-not-allowed data-[state=checked]:font-medium data-[disabled]:opacity-50"
                >
                  <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator>
                    <Mini.CheckIcon
                      className="text-accent size-4"
                      aria-hidden="true"
                    />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
              {actions.length > 0 ? (
                <>
                  <RadixSelect.Separator className="bg-separator my-1 h-px" />
                  {actions.map((action, index) => (
                    <RadixSelect.Item
                      key={`${action.label}-${index}`}
                      value={getActionValue(index)}
                      disabled={action.disabled}
                      data-ths-select-action={action.tone ?? "default"}
                      className={cn(
                        "text-body data-[highlighted]:bg-fill flex cursor-pointer select-none items-center gap-2 px-3 py-2 outline-none",
                        "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
                        actionToneClassName[action.tone ?? "default"],
                      )}
                    >
                      {action.icon ? (
                        <span className="shrink-0" aria-hidden="true">
                          {action.icon}
                        </span>
                      ) : null}
                      <RadixSelect.ItemText>
                        {action.label}
                      </RadixSelect.ItemText>
                    </RadixSelect.Item>
                  ))}
                </>
              ) : null}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
      {error ? (
        <p id={`${labelId}-error`} className="text-caption text-error-text">
          {error}
        </p>
      ) : helperText ? (
        <p id={`${labelId}-help`} className="text-caption text-text-secondary">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
